var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { forwardRef, useRef, useState, } from "react";
import { usePopper } from "react-popper";
import FocusTrap from "focus-trap-react";
import ReactDOM from "react-dom";
import { THEME } from "./theming/theming";
import { Flex } from "./Flex/Flex";
import { View } from "./View/View";
import { Button } from "./Button";
import { UtilityStyle } from "./hooks/useUtilityStyles";
export var OverlayTriggerType;
(function (OverlayTriggerType) {
    OverlayTriggerType["Hover"] = "hover";
    OverlayTriggerType["Click"] = "click";
    OverlayTriggerType["None"] = "none";
})(OverlayTriggerType || (OverlayTriggerType = {}));
export var OverlayStyle;
(function (OverlayStyle) {
    OverlayStyle["Popover"] = "popover";
    OverlayStyle["Dropdown"] = "dropdown";
    OverlayStyle["Popout"] = "popout";
})(OverlayStyle || (OverlayStyle = {}));
export var OverlayTrigger = function (_a) {
    var ariaLabel = _a.ariaLabel, container = _a.container, displayArrow = _a.displayArrow, overlayContent = _a.overlayContent, overlayTitle = _a.overlayTitle, overlayTrigger = _a.overlayTrigger, _b = _a.placement, placement = _b === void 0 ? "bottom" : _b, _c = _a.strategy, strategy = _c === void 0 ? "fixed" : _c, _d = _a.triggerType, triggerType = _d === void 0 ? OverlayTriggerType.Click : _d, overlayStyle = _a.overlayStyle, onOutsideClickDeactivate = _a.onOutsideClickDeactivate, dataTestId = _a.dataTestId, size = _a.size, onClose = _a.onClose, closeButton = _a.closeButton, open = _a.open, targetRef = _a.targetRef;
    var _e = useState(null), triggerElement = _e[0], setTriggerElement = _e[1];
    var _f = useState(null), popperElement = _f[0], setPopperElement = _f[1];
    var _g = useState(null), arrowElement = _g[0], setArrowElement = _g[1];
    var _h = useState(false), show = _h[0], setShow = _h[1];
    var _j = useState(false), focused = _j[0], setFocused = _j[1];
    var options = { modifiers: [], placement: placement, strategy: strategy };
    var triggerProps = {};
    var isControlled = triggerType === OverlayTriggerType.None && open !== undefined;
    var isArrowDisplayed = displayArrow ||
        (displayArrow === undefined && overlayStyle === OverlayStyle.Popover);
    if (isArrowDisplayed) {
        options.modifiers.push({
            name: "arrow",
            options: { element: arrowElement },
        });
        options.modifiers.push({ name: "offset", options: { offset: [0, 11] } });
    }
    var _k = usePopper(triggerElement || (targetRef === null || targetRef === void 0 ? void 0 : targetRef.current), popperElement, options), styles = _k.styles, attributes = _k.attributes;
    var handleClick = function () {
        setShow(!show);
    };
    var handleMouseEnter = function () {
        setShow(true);
    };
    var handleMouseLeave = function () {
        setShow(false);
    };
    var handleFocus = function () {
        setShow(true);
    };
    var handleBlur = function () {
        if (show && !focused) {
            handleOnClose();
        }
    };
    var handleOnClose = function () {
        setShow(false);
        onClose && onClose();
    };
    if (triggerType === OverlayTriggerType.Click) {
        triggerProps.onClick = handleClick;
    }
    else if (triggerType === OverlayTriggerType.Hover) {
        triggerProps.onMouseEnter = handleMouseEnter;
        triggerProps.onFocus = handleFocus;
        triggerProps.onMouseLeave = handleMouseLeave;
        triggerProps.onBlur = handleBlur;
    }
    var overlay = (React.createElement(Overlay, { overlayContent: overlayContent, overlayTitle: overlayTitle, overlayStyle: overlayStyle, styles: styles, attributes: attributes.popper, popperRef: setPopperElement, arrowRef: setArrowElement, isArrowDisplayed: isArrowDisplayed, closePopover: function () { return handleOnClose(); }, setFocused: function (focus) { return setFocused(focus); }, onOutsideClickDeactivate: onOutsideClickDeactivate, dataTestId: dataTestId, ariaLabel: ariaLabel, triggerEl: triggerElement, size: size, closeButton: closeButton }));
    var shouldShow = isControlled ? open : show;
    return (React.createElement(React.Fragment, null,
        targetRef ? (overlayTrigger) : (React.createElement("span", __assign({ className: "popper-trigger", ref: setTriggerElement }, triggerProps), overlayTrigger)),
        shouldShow && !(container === null || container === void 0 ? void 0 : container.current) && overlay,
        shouldShow &&
            (container === null || container === void 0 ? void 0 : container.current) &&
            ReactDOM.createPortal(overlay, container.current)));
};
var Overlay = function (props) {
    var closePopover = props.closePopover, onOutsideClickDeactivate = props.onOutsideClickDeactivate, setFocused = props.setFocused, triggerEl = props.triggerEl;
    var contentRef = useRef(null);
    var handleClose = function () {
        setFocused(false);
        closePopover();
        if (onOutsideClickDeactivate) {
            onOutsideClickDeactivate();
        }
    };
    var handleClick = function (event) {
        if (!(props === null || props === void 0 ? void 0 : props.closeButton) && event.target !== contentRef.current)
            closePopover();
    };
    var handleKeyPress = function (event) {
        if (event.which === 32) {
            handleClose();
        }
    };
    var onClickOutside = function (event) {
        if (triggerEl && event.target && triggerEl.contains(event.target)) {
            return true;
        }
        handleClose();
        return true;
    };
    return (React.createElement(FocusTrap, { focusTrapOptions: {
            onActivate: function () { return setFocused(true); },
            clickOutsideDeactivates: onClickOutside,
            escapeDeactivates: function () {
                handleClose();
                return true;
            },
            initialFocus: false,
            returnFocusOnDeactivate: false,
            fallbackFocus: ".popper-overlay",
        } },
        React.createElement(OverlayTriggerContent, __assign({}, props, { ref: contentRef, onClick: handleClick, onKeyPress: handleKeyPress, onClose: function () { return closePopover(); } }))));
};
export var OverlayTriggerContent = forwardRef(function (props, contentRef) { return (React.createElement("div", __assign({ "aria-label": props === null || props === void 0 ? void 0 : props.ariaLabel, "aria-hidden": true, ref: props === null || props === void 0 ? void 0 : props.popperRef, onClick: props === null || props === void 0 ? void 0 : props.onClick, onKeyPress: props === null || props === void 0 ? void 0 : props.onKeyPress, style: (props === null || props === void 0 ? void 0 : props.size)
        ? __assign(__assign({}, props === null || props === void 0 ? void 0 : props.styles.popper), { maxWidth: getSize(props === null || props === void 0 ? void 0 : props.size), width: getSize(props === null || props === void 0 ? void 0 : props.size) }) : props === null || props === void 0 ? void 0 : props.styles.popper }, props === null || props === void 0 ? void 0 : props.attributes, { className: "popper-overlay popper-".concat(props === null || props === void 0 ? void 0 : props.overlayStyle), tabIndex: -1, "data-testid": props === null || props === void 0 ? void 0 : props.dataTestId }),
    React.createElement(Flex, { direction: "column", ref: contentRef },
        (props === null || props === void 0 ? void 0 : props.overlayTitle) && (React.createElement(View, { border: true, borderWidthTop: "zero", borderWidthLeft: "zero", borderWidthRight: "zero" },
            React.createElement(Flex, null,
                React.createElement(View, { flex: 1, paddingTop: "medium", paddingBottom: "medium", paddingLeft: "large", paddingRight: "large" },
                    React.createElement("strong", null, props === null || props === void 0 ? void 0 : props.overlayTitle)),
                (props === null || props === void 0 ? void 0 : props.closeButton) && (React.createElement(View, { paddingRight: "large" },
                    React.createElement(Button, { label: "Close", onClick: props === null || props === void 0 ? void 0 : props.onClose, type: "suppressed", styleOptions: [
                            UtilityStyle.VerticalPaddingSmall,
                            UtilityStyle.TopSpaceSmall,
                        ] })))))),
        (props === null || props === void 0 ? void 0 : props.overlayStyle) !== OverlayStyle.Popout ? (React.createElement("div", { ref: contentRef, className: "popper-".concat(props === null || props === void 0 ? void 0 : props.overlayStyle, "-content") }, props === null || props === void 0 ? void 0 : props.overlayContent)) : (React.createElement(View, null, props === null || props === void 0 ? void 0 : props.overlayContent))),
    (props === null || props === void 0 ? void 0 : props.isArrowDisplayed) && (props === null || props === void 0 ? void 0 : props.overlayStyle) !== OverlayStyle.Popout && (React.createElement("div", { ref: props === null || props === void 0 ? void 0 : props.arrowRef, id: "popper-arrow", className: (props === null || props === void 0 ? void 0 : props.overlayTitle) ? "has-title" : "", "data-testid": "popper-arrow", style: props === null || props === void 0 ? void 0 : props.styles.arrow })))); });
var getSize = function (size) {
    if (size === "large")
        return THEME.sizes.large;
    if (size === "medium")
        return THEME.sizes.medium;
    if (size === "smallplus")
        return THEME.sizes.smallplus;
    if (size === "small")
        return THEME.sizes.small;
    return THEME.sizes.small;
};
